import { useId, useState } from 'react';
import { cx } from '../lib/cx';

export interface BarDatum {
  key: string;
  label: string;
  value: number;
  /** Formatirana vrijednost uz vrh trake. Vrijednost se nikad ne čita samo iz tooltipa. */
  display: string;
}

export interface BarListLabels {
  label: string;
  value: string;
  showTable: string;
  hideTable: string;
}

export interface BarListProps {
  data: readonly BarDatum[];
  ariaLabel: string;
  labels: BarListLabels;
  className?: string;
}

/**
 * Vodoravni stupčasti graf za usporedbu veličina.
 *
 * Jedna serija → jedna boja iz tokena (`accent`), bez legende: naslov kartice
 * već kaže što se crta. Vrijednost stoji uz vrh trake i u tabličnom blizancu,
 * pa se nikad ne čita isključivo iz boje ni iz tooltipa.
 */
export function BarList({ data, ariaLabel, labels, className }: BarListProps) {
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  const max = Math.max(1, ...data.map((d) => (Number.isFinite(d.value) ? d.value : 0)));

  return (
    <figure className={cx('m-0 flex flex-col gap-4', className)} data-testid="bar-list">
      <ul className="flex flex-col gap-2.5" aria-label={ariaLabel}>
        {data.map((d) => {
          const value = Number.isFinite(d.value) ? Math.max(0, d.value) : 0;
          const pct = (value / max) * 100;
          return (
            <li
              key={d.key}
              tabIndex={0}
              aria-label={`${d.label}: ${d.display}`}
              data-value={value}
              className="group relative flex items-center gap-3 rounded-md"
            >
              <span className="w-28 shrink-0 truncate text-[13px] text-ink-muted sm:w-40">
                {d.label}
              </span>
              {/* Traka: podloga je korito, ispuna nosi vrijednost. */}
              <span className="relative h-3 min-w-0 flex-1 rounded-r-[4px] bg-bg-alt">
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 rounded-r-[4px] bg-accent"
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span className="w-16 shrink-0 text-right text-[13px] tabular-nums text-ink">
                {d.display}
              </span>
              <span
                aria-hidden="true"
                className={cx(
                  'pointer-events-none absolute -top-8 right-0 z-10 rounded-md bg-inverse px-3 py-1.5',
                  'text-[12px] text-ink-ondark opacity-0 shadow-overlay transition-opacity',
                  'group-hover:opacity-100 group-focus-within:opacity-100',
                )}
              >
                {d.label}: {d.display}
              </span>
            </li>
          );
        })}
      </ul>

      <div>
        <button
          type="button"
          aria-expanded={showTable}
          aria-controls={tableId}
          onClick={() => setShowTable((v) => !v)}
          className="text-[12px] text-accent-dark underline"
        >
          {showTable ? labels.hideTable : labels.showTable}
        </button>
      </div>

      {showTable && (
        <div id={tableId} className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{ariaLabel}</caption>
            <thead>
              <tr className="border-b border-line-subtle">
                <th scope="col" className="py-2 text-[11px] uppercase tracking-[2px] text-ink-muted">
                  {labels.label}
                </th>
                <th
                  scope="col"
                  className="py-2 text-right text-[11px] uppercase tracking-[2px] text-ink-muted"
                >
                  {labels.value}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.key} className="border-b border-line-subtle last:border-b-0">
                  <th scope="row" className="py-2 text-[14px] font-normal text-ink">
                    {d.label}
                  </th>
                  <td className="py-2 text-right text-[14px] tabular-nums text-ink">{d.display}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <figcaption className="sr-only">{ariaLabel}</figcaption>
    </figure>
  );
}
